import { useEffect, useState } from "react";
import {
    ActiveFilterChips, Card, ChipPopover, FilterBar, FilterDivider, FilterMenu, Input, ds,
    type AppliedChip, type FilterOption,
} from "@/shared/components/ds";
import { LaunchPicker } from "../LaunchPicker";
import type { DashboardFilters, LaunchDashboardFilterOptions, LaunchOption } from "../models/dashboard";
import { CommandGetLaunchDashboardFilters } from "../services/cmdGetLaunchDashboardFilters";
import {
    clampRange, daysBefore, DEFAULT_DASHBOARD_FILTERS, formatDay, MAX_RANGE_DAYS, pickerNarrowed, RANGE_PRESETS, STOP_DAY, type Preset,
} from "./dashboardFilters";

interface Props {
    /** What the widgets show now, from the URL. */
    filters: DashboardFilters;
    onApply: (next: DashboardFilters) => void;
    /** The launch's own span, once its record is known; the Launch Lifetime preset needs it. */
    lifetime?: { from: string; to: string } | null;
    /** The picker's pool, as the three narrowing filters leave it. */
    launches: LaunchOption[];
    launchesLoading?: boolean;
    /** Bump to open the picker from outside (the empty state's "Choose a Launch"). */
    openRequest?: number;
}

/** The people selects, with OP's two synthetic rows first. */
const ALL = "-1";
const UNASSIGNED = "-2";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const shortDay = (day: string) => formatDay(day).replace(/, \d{4}$/, "");

/**
 * The dashboard's filters (MPN-2230, MPN-2313). The launch picker leads: it searches by
 * name, ID and affiliate. Date Range scopes the widgets, with fixed presets counted back
 * from the stop day and a custom pair capped at a year; the other three narrow the picker.
 * Every change writes the URL at once, so what is shown is always what the link says.
 */
export function DashboardFilterBar({ filters, onApply, lifetime, launches, launchesLoading = false, openRequest = 0 }: Props) {
    const [options, setOptions] = useState<LaunchDashboardFilterOptions>({});
    const [dateOpen, setDateOpen] = useState(false);

    useEffect(() => {
        new CommandGetLaunchDashboardFilters().run().then(setOptions).catch(() => setOptions({}));
    }, []);

    const set = (patch: Partial<DashboardFilters>) => onApply({ ...filters, ...patch });

    const presets: Preset[] = [
        ...RANGE_PRESETS,
        ...(lifetime ? [{ key: "lifetime", label: "Launch Lifetime", ...lifetime }] : []),
    ];
    const preset = presets.find((p) => p.from === filters.start_date && p.to === filters.end_date);
    const isDefault = filters.start_date === DEFAULT_DASHBOARD_FILTERS.start_date && filters.end_date === DEFAULT_DASHBOARD_FILTERS.end_date;
    const rangeText = `${formatDay(filters.start_date)} to ${formatDay(filters.end_date)}`;
    const dateLabel = preset ? preset.label : rangeText;

    const people = (list?: { id: number; name: string }[] | null): FilterOption[] => [
        { value: ALL, label: "All" },
        ...(list ?? []).filter((p) => String(p.id) !== ALL && String(p.id) !== UNASSIGNED).map((p) => ({ value: String(p.id), label: p.name })),
        { value: UNASSIGNED, label: "Unassigned" },
    ];
    const types: FilterOption[] = (options.revenue_types ?? []).map((o) => ({ value: String(o.value), label: o.label }));
    const oms = people(options.ops_managers);
    const ems = people(options.engagement_managers);
    const labelOf = (list: FilterOption[], value: string) => list.find((o) => o.value === value)?.label ?? value;

    const chips: AppliedChip[] = [];
    if (!isDefault) chips.push({ label: `Date Range: ${dateLabel}`, remove: () => set({ start_date: DEFAULT_DASHBOARD_FILTERS.start_date, end_date: DEFAULT_DASHBOARD_FILTERS.end_date }) });
    if (filters.ops_manager !== ALL) chips.push({ label: `Ops Manager: ${labelOf(oms, filters.ops_manager)}`, remove: () => set({ ops_manager: ALL }) });
    if (filters.engagement_manager !== ALL) chips.push({ label: `Engagement Manager: ${labelOf(ems, filters.engagement_manager)}`, remove: () => set({ engagement_manager: ALL }) });
    if (filters.revenue_type !== DEFAULT_DASHBOARD_FILTERS.revenue_type) chips.push({ label: `Type: ${labelOf(types, filters.revenue_type)}`, remove: () => set({ revenue_type: DEFAULT_DASHBOARD_FILTERS.revenue_type }) });

    const onDay = (moved: "from" | "to") => (e: React.ChangeEvent<HTMLInputElement>) => {
        // Only a whole day reaches the URL, so a half-typed input never asks the warehouse for "2026-1".
        if (!ISO_DAY.test(e.target.value)) return;
        const from = moved === "from" ? e.target.value : filters.start_date;
        const to = moved === "to" ? e.target.value : filters.end_date;
        set(clampRange(from, to, moved));
    };
    // The inputs' own bounds say the rules before a day is typed; clampRange holds them after.
    const fromMin = daysBefore(filters.end_date, MAX_RANGE_DAYS);
    const toMaxByWindow = daysBefore(filters.start_date, -MAX_RANGE_DAYS);
    const toMax = toMaxByWindow < STOP_DAY ? toMaxByWindow : STOP_DAY;

    return (
        <Card clip={false}>
            <FilterBar hint="Date Range scopes the widgets. The other filters narrow the launch picker.">
                <LaunchPicker launches={launches} value={filters.launch_id} onPick={(launch_id) => set({ launch_id })}
                    filtered={pickerNarrowed(filters)} openRequest={openRequest} loading={launchesLoading} width={340} />
                <FilterDivider />
                <ChipPopover label={`Date Range: ${dateLabel}`} active={!isDefault} icon={<i className="bi bi-calendar3" aria-hidden="true" />}
                    open={dateOpen} onOpenChange={setDateOpen} popWidth={340} title="Date Range">
                    <div style={{ display: "flex", flexDirection: "column", padding: "0 6px 6px" }} role="listbox" aria-label="Date Range">
                        {presets.map((p) => (
                            <button key={p.key} type="button" role="option" aria-selected={preset?.key === p.key} className={ds.popOption}
                                onClick={() => { set({ start_date: p.from, end_date: p.to }); setDateOpen(false); }}>
                                <span>{p.label}</span>
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className={ds.hint}>{shortDay(p.from)} to {formatDay(p.to)}</span>
                                    {preset?.key === p.key && <i className={`bi bi-check2 ${ds.check}`} aria-hidden="true" />}
                                </span>
                            </button>
                        ))}
                        {!lifetime && (
                            <div className={ds.popNone}>Launch Lifetime is available once the launch's record has loaded.</div>
                        )}
                        <button type="button" role="option" aria-selected={!preset} className={ds.popOption}>
                            <span>Custom range</span>
                            {!preset && <i className={`bi bi-check2 ${ds.check}`} aria-hidden="true" />}
                        </button>
                    </div>
                    <div className={ds.popBody}>
                        <Input label="From" type="date" small min={fromMin} max={filters.end_date} defaultValue={filters.start_date} onChange={onDay("from")} key={`from-${filters.start_date}-${filters.end_date}`} />
                        <Input label="To" type="date" small min={filters.start_date} max={toMax} defaultValue={filters.end_date} onChange={onDay("to")} key={`to-${filters.start_date}-${filters.end_date}`} />
                    </div>
                    <div className={ds.popNone}>The data stops on {formatDay(STOP_DAY)}. A custom range spans at most a year.</div>
                </ChipPopover>
                <FilterDivider />
                <FilterMenu kind="single" label="Ops Manager" options={oms} value={filters.ops_manager} allValue={ALL} searchable
                    placeholder="e.g. Luis Bautista" onChange={(ops_manager) => set({ ops_manager })} />
                <FilterMenu kind="single" label="Engagement Manager" options={ems} value={filters.engagement_manager} allValue={ALL} searchable
                    placeholder="e.g. Jenna Morales" onChange={(engagement_manager) => set({ engagement_manager })} />
                <FilterMenu kind="single" label="Type" options={types} value={filters.revenue_type}
                    allValue={DEFAULT_DASHBOARD_FILTERS.revenue_type} onChange={(revenue_type) => set({ revenue_type })} />
            </FilterBar>
            <ActiveFilterChips chips={chips} edge="top"
                onClearAll={() => onApply({ ...DEFAULT_DASHBOARD_FILTERS, launch_id: filters.launch_id })} />
        </Card>
    );
}
